import React from 'react';
import { Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface MissionBannerProps {
    visible: boolean;
    label: string;
    focus: string;
    minutes: number | string;
    module: 'osp' | 'zsv'; // To style differently if needed, or just for context
    onStart: () => void;
    onDismiss: () => void;
    onComplete: () => void;
    className?: string;
}

export const MissionBanner: React.FC<MissionBannerProps> = ({
    visible,
    label,
    focus,
    minutes,
    onStart,
    onDismiss,
    onComplete,
    className
}) => {
    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: -20, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -20, height: 0 }}
                    className={`
                        fixed top-16 left-0 right-0 z-40 p-4 
                        bg-background/95 backdrop-blur-md border-b border-border shadow-md
                        md:static md:w-full md:max-w-3xl md:mx-auto md:mt-4 md:px-0 md:bg-transparent md:border-0 md:shadow-none md:z-0
                        ${className || ''}
                    `}
                >
                    <div className="
                        flex flex-col sm:flex-row items-center justify-between gap-4 
                        md:p-4 md:bg-card md:border md:border-border md:rounded-xl md:shadow-sm
                    ">
                        <div className="flex items-center gap-3 w-full sm:w-auto relative">
                            {/* Dismiss Button (Mobile: Top Right absolute, Desktop: inline handled differently? No, keeps consistent) */}
                            {/* Actually, for the card design, a small X in top right might be better, or a "Teď ne" button. 
                                 The req says "Mission banner must have 'Teď ne' (or X)". 
                                 Let's put 'Teď ne' in the button row for clarity, and maybe an X for quick dismiss on mobile.
                             */}

                            <div className="p-2 bg-brand-orange/10 rounded-full flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-brand-orange" />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm sm:text-base">
                                    {label}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    {minutes} min • {focus === 'bzs' ? 'ZSV' : focus}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button
                                onClick={onDismiss}
                                className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary rounded-lg transition-colors whitespace-nowrap"
                            >
                                Teď ne
                            </button>
                            <button
                                onClick={onComplete}
                                className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg border border-transparent hover:border-green-200 transition-colors whitespace-nowrap"
                            >
                                ✓ Označit jako hotovo
                            </button>
                            <button
                                onClick={onStart}
                                className="flex-1 sm:flex-none px-4 py-1.5 text-xs font-bold bg-brand-orange text-white rounded-lg shadow-sm hover:opacity-90 transition-opacity whitespace-nowrap"
                            >
                                Začít teď
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
